import React, { useMemo, useEffect } from 'react';
import { Spinner } from '../../../common/index.jsx';
import ArticleContent from './ArticleContent.jsx';
import usePermission from '../../../../hooks/usePermission.jsx';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetArticleQuery } from '../../../../redux/services/articles.jsx';
import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { i18n } = useTranslation();
  const { language } = i18n;
  const showEditIcon = usePermission('articles.show');

  const initialPage = useMemo(() => {
    const pageSlugEn = window?.initData?.article?.slug_en;
    const pageSlugSk = window?.initData?.article?.slug_sk;

    // if pageSlug is not undefined AND slug and pageSlug are same
    // return window.initData.customPage
    // otherwise return undefined => fetching is triggered automatically
    return (slug === pageSlugEn || slug === pageSlugSk)
      ? window.initData.article
      : undefined;
  }, [slug]);

  const { data: articleData } = useGetArticleQuery(
    { id: slug, isEdit: false },
    { skip: !isUndefined(initialPage) },
  );

  const articlePageData = initialPage || articleData;
  const languageFromSlug = useMemo(() => {
    if (!articlePageData) {
      return null;
    }

    const hasDistinctLanguageSlugs =
      !!articlePageData.slug_en &&
      !!articlePageData.slug_sk &&
      articlePageData.slug_en !== articlePageData.slug_sk;

    if (!hasDistinctLanguageSlugs) {
      return null;
    }

    if (!!articlePageData.slug_en && slug === articlePageData.slug_en) {
      return 'en';
    }

    if (!!articlePageData.slug_sk && slug === articlePageData.slug_sk) {
      return 'sk';
    }

    return null;
  }, [articlePageData, slug]);

  const isEnglishVersion = languageFromSlug
    ? languageFromSlug === 'en'
    : language === 'en';

  useEffect(() => {
    if (!languageFromSlug || i18n.language === languageFromSlug) {
      return;
    }

    i18n.changeLanguage(languageFromSlug);
  }, [i18n, languageFromSlug]);

  // Set page title to the localized article title when data is available
  useEffect(() => {
    if (!articlePageData) return;
    const activeLanguage = languageFromSlug || language;
    const localizedTitle =
      activeLanguage === 'en'
        ? articlePageData.title_en || articlePageData.title_sk || ''
        : articlePageData.title_sk || articlePageData.title_en || '';
    if (localizedTitle) {
      document.title = localizedTitle;
    }
  }, [articlePageData, language, languageFromSlug]);

  if (!articlePageData) {
    return <Spinner />;
  }

  return (
    <ArticleContent
      articlePage={articlePageData}
      isEnglishVersion={isEnglishVersion}
      showEditIcon={showEditIcon}
      redirectToPage={(url) => navigate(url, { state: { from: location } })}
    />
  );
};

export default ArticlePage;
